import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export const EASE = [0.22, 1, 0.36, 1];

export const scrollToHash = (hash) => {
  const lenis = window.__lenis;
  if (lenis) {
    lenis.scrollTo(hash, { offset: -72, duration: 1.6 });
  } else {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const FadeUp = ({ children, delay = 0, y = 30, duration = 0.7, className = "", amount = 0.2 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount }}
    transition={{ duration, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <motion.span
    className="block overflow-hidden"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.3 }}
    variants={{ hidden: {}, visible: {} }}
  >
    <motion.span
      className={`block ${className}`}
      variants={{
        hidden: { y: "112%" },
        visible: { y: "0%", transition: { duration: 0.9, delay, ease: EASE } },
      }}
    >
      {children}
    </motion.span>
  </motion.span>
);

export const SectionLabel = ({ children, className = "" }) => (
  <p
    className={`font-sans font-normal text-[11px] tracking-[0.22em] uppercase text-champagne ${className}`}
  >
    {children}
  </p>
);

export const ChapterLabel = ({ num, label, className = "" }) => (
  <FadeUp className={`flex items-center gap-4 ${className}`} y={16} duration={0.6}>
    <span className="font-display font-medium text-[10px] tracking-[0.2em] text-muted">
      {num}
    </span>
    <span className="h-px w-10 bg-champagne/40" aria-hidden="true" />
    <SectionLabel>{label}</SectionLabel>
  </FadeUp>
);

export const CTAButton = ({
  children,
  variant = "primary",
  onClick,
  href,
  testid,
  className = "",
  type = "button",
  disabled = false,
}) => {
  const base =
    "group inline-flex items-center justify-center gap-3 font-display font-semibold text-[11px] tracking-[0.14em] uppercase rounded-[2px] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-champagne text-[#0a0a0a] px-[22px] py-[14px] hover:bg-ivory"
      : "border border-white/25 text-ivory px-[22px] py-[14px] hover:border-champagne/70 hover:text-champagne";
  const inner = (
    <>
      <span>{children}</span>
      <ArrowRight
        className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
        aria-hidden="true"
      />
    </>
  );
  if (href) {
    return (
      <a href={href} data-testid={testid} className={`${base} ${styles} ${className}`}>
        {inner}
      </a>
    );
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`${base} ${styles} ${className}`}
    >
      {inner}
    </button>
  );
};

export const Container = ({ children, className = "" }) => (
  <div className={`mx-auto w-full max-w-[1440px] px-6 md:px-12 lg:px-20 ${className}`}>
    {children}
  </div>
);
